(function () {
  'use strict';

  class ProductsListCtaCard {
    constructor(card, section) {
      this.card = card;
      this.section = section;
      this.ctaAction = section?.dataset.ctaAction || 'add_to_cart';
      this.linkBehavior = section?.dataset.linkBehavior || 'none';
      this.blockId = card.dataset.blockId;
      this.mainImage = card.querySelector('[data-main-image]');
      this.imageLoader = card.querySelector('[data-image-loader]');
      this.variantInput = card.querySelector('[data-variant-id-input]');
      this.priceElement = card.querySelector('[data-product-price]');
      this.addToCartButton = card.querySelector('[data-add-to-cart]');
      this.addToCartText = card.querySelector('[data-add-to-cart-text]');
      this.productLink = card.querySelector('[data-product-link]');
      this.variantThumbs = Array.from(card.querySelectorAll('[data-variant-thumb]'));
      this.form = card.querySelector('form');

      this.ctaText = card?.dataset.ctaText || 'ADD TO CART';
      this.soldOutText = section?.dataset.soldOutText || 'SOLD OUT';

      this.modal = null;
      this.focusedElementBeforeModal = null;
      this._handleFocusTrap = this._handleFocusTrap.bind(this);
      this._handleEscape = this._handleEscape.bind(this);

      this.init();
    }

    init() {
      this.bindVariantThumbs();

      if (this.ctaAction === 'add_to_cart') {
        this.initAjaxProduct();
      }

      if (this.linkBehavior === 'modal') {
        this.initModal();
      }
    }

    initModal() {
      if (!this.section || !this.blockId) return;
      this.modal = this.section.querySelector(`[data-products-list-cta-modal="${this.blockId}"]`);
      if (!this.modal) return;

      const triggers = this.card.querySelectorAll('.js-products-list-cta-modal-trigger');
      triggers.forEach((trigger) => {
        trigger.addEventListener('click', (e) => {
          e.preventDefault();
          this.openModal();
        });
      });

      const closeButtons = this.modal.querySelectorAll('[data-modal-close]');
      closeButtons.forEach((btn) => {
        btn.addEventListener('click', () => this.closeModal());
      });

      document.addEventListener('keydown', this._handleEscape);
    }

    openModal() {
      if (!this.modal) return;

      this.focusedElementBeforeModal = document.activeElement;

      this.modal.hidden = false;
      this.modal.setAttribute('aria-hidden', 'false');
      this.modal.offsetHeight;
      this.modal.classList.add('is-active');

      document.body.style.overflow = 'hidden';
      this._setPageInert(true);
      document.addEventListener('keydown', this._handleFocusTrap);

      this.modal.setAttribute('tabindex', '-1');
      this.modal.focus();

      const closeButton = this.modal.querySelector('[data-modal-close]');
      if (closeButton) {
        setTimeout(() => closeButton.focus(), 100);
      }
    }

    closeModal() {
      if (!this.modal) return;

      document.removeEventListener('keydown', this._handleFocusTrap);
      this._setPageInert(false);
      this.modal.classList.remove('is-active');

      setTimeout(() => {
        if (!this.modal) return;
        this.modal.hidden = true;
        this.modal.setAttribute('aria-hidden', 'true');
        this.modal.removeAttribute('tabindex');
        document.body.style.overflow = '';

        if (this.focusedElementBeforeModal && typeof this.focusedElementBeforeModal.focus === 'function') {
          this.focusedElementBeforeModal.focus();
        }
        this.focusedElementBeforeModal = null;
      }, 300);
    }

    _handleEscape(e) {
      if (e.key === 'Escape' && this.modal && !this.modal.hidden) {
        this.closeModal();
      }
    }

    _handleFocusTrap(e) {
      if (e.key !== 'Tab' || !this.modal) return;

      const focusableElements = this.modal.querySelectorAll(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      const focusableArray = Array.from(focusableElements).filter((el) => el.offsetParent !== null);
      if (focusableArray.length === 0) return;

      const firstFocusable = focusableArray[0];
      const lastFocusable = focusableArray[focusableArray.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === firstFocusable || document.activeElement === this.modal) {
          e.preventDefault();
          lastFocusable.focus();
        }
      } else {
        if (document.activeElement === lastFocusable) {
          e.preventDefault();
          firstFocusable.focus();
        }
      }
    }

    _setPageInert(inert) {
      const bodyChildren = document.body.children;
      for (let i = 0; i < bodyChildren.length; i++) {
        const child = bodyChildren[i];
        if (
          child.contains(this.modal) ||
          child.tagName === 'SCRIPT' ||
          child.tagName === 'STYLE' ||
          child.tagName === 'LINK'
        ) {
          continue;
        }
        if (inert) {
          child.setAttribute('inert', '');
          child.setAttribute('aria-hidden', 'true');
        } else {
          child.removeAttribute('inert');
          child.removeAttribute('aria-hidden');
        }
      }
    }

    initAjaxProduct() {
      if (!this.form || !window.theme || !window.theme.AjaxProduct) return;

      try {
        new window.theme.AjaxProduct(this.form, '[data-add-to-cart]');
      } catch (error) {
        console.warn('ProductsListCta: AjaxProduct initialization skipped for one card.', error);
      }
    }

    bindVariantThumbs() {
      if (!this.variantThumbs.length) return;

      this.variantThumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => {
          this.selectVariant(thumb);
        });
      });
    }

    selectVariant(thumb) {
      const variantId = thumb.dataset.variantId;
      const variantPrice = thumb.dataset.variantPrice;
      const variantComparePrice = thumb.dataset.variantComparePrice;
      const variantAvailable = thumb.dataset.variantAvailable === 'true';
      const variantImage = thumb.dataset.variantImage;
      const variantImageAlt = thumb.dataset.variantImageAlt;

      if (this.variantInput && variantId) {
        this.variantInput.value = variantId;
      }

      // Update price using component-product-price structure
      if (this.priceElement && variantPrice) {
        this.priceElement.innerHTML = variantPrice;

        // Handle compare price and sale styling
        const priceWrapper = this.card.querySelector('.product-price');
        if (priceWrapper) {
          const comparePriceElement = priceWrapper.querySelector('[data-compare-price]');
          const comparePriceWrap = priceWrapper.querySelector('[data-product-price-wrap]');
          const savingsElement = priceWrapper.querySelector('[data-save-price]');

          const hasComparePrice = variantComparePrice && variantComparePrice !== '' && variantComparePrice !== variantPrice;

          if (hasComparePrice) {
            // Show sale price
            if (comparePriceElement) {
              comparePriceElement.innerHTML = variantComparePrice;
            }
            if (comparePriceWrap) {
              comparePriceWrap.classList.remove('hide');
            }
            this.priceElement.classList.add('product-price__price--on-sale');

            // Hide savings for now (would need calculation)
            if (savingsElement) {
              savingsElement.classList.add('product-price__savings--hidden');
            }
          } else {
            // Hide sale price
            if (comparePriceWrap) {
              comparePriceWrap.classList.add('hide');
            }
            this.priceElement.classList.remove('product-price__price--on-sale');

            if (savingsElement) {
              savingsElement.classList.add('product-price__savings--hidden');
            }
          }
        }
      }

      if (this.mainImage && variantImage) {
        // Show loader
        if (this.imageLoader) {
          this.imageLoader.classList.add('is-loading');
        }

        // Create a new image to preload
        const newImage = new Image();
        newImage.onload = () => {
          this.mainImage.src = variantImage;
          if (variantImageAlt) {
            this.mainImage.alt = variantImageAlt;
          }
          // Hide loader
          if (this.imageLoader) {
            this.imageLoader.classList.remove('is-loading');
          }
        };
        newImage.onerror = () => {
          // Hide loader even on error
          if (this.imageLoader) {
            this.imageLoader.classList.remove('is-loading');
          }
        };
        newImage.src = variantImage;
      }

      if (this.addToCartButton) {
        this.addToCartButton.disabled = !variantAvailable;
      }

      if (this.addToCartText) {
        this.addToCartText.textContent = variantAvailable ? this.ctaText : this.soldOutText;
        if (this.addToCartButton) {
          this.addToCartButton.setAttribute('aria-label', this.addToCartText.textContent);
        }
      }

      if (this.productLink && variantId) {
        const baseUrl = this.productLink.dataset.productUrlBase || this.productLink.getAttribute('href') || '';
        this.productLink.href = this.buildVariantUrl(baseUrl, variantId);
      }

      this.variantThumbs.forEach((item) => {
        item.classList.remove('is-active');
        item.setAttribute('aria-pressed', 'false');
      });

      thumb.classList.add('is-active');
      thumb.setAttribute('aria-pressed', 'true');
    }

    buildVariantUrl(baseUrl, variantId) {
      try {
        const url = new URL(baseUrl, window.location.origin);
        url.searchParams.set('variant', variantId);
        return `${url.pathname}${url.search}`;
      } catch (error) {
        return `${baseUrl}?variant=${variantId}`;
      }
    }
  }

  /**
   * Initialize Splide slider for Products List CTA
   * - Slider enabled on mobile (all cases)
   * - Slider enabled on desktop only when 4+ blocks
   * - Grid layout on desktop when < 4 blocks
   */
  window.initProductsListCtaSlider = function (container) {
    const slider = container.querySelector('.js-products-list-cta-slider');

    if (!slider || typeof Splide === 'undefined') return null;

    const blockCount = parseInt(container.dataset.blockCount || '0');
    let splideInstance = null;

    const shouldEnableSlider = () => {
      const isMobile = window.innerWidth < 1100;
      const hasEnoughBlocks = blockCount >= 4;
      return isMobile || hasEnoughBlocks;
    };

    // Initialize slider (always mounted, but may be disabled)
    splideInstance = window.SplideUtils.initSplideWithControls(
      slider,
      container,
      {
        perPage: 3,
        perMove: 1,
        gap: '24px',
        arrows: false,
        pagination: false,
        rewind: true,
        drag: shouldEnableSlider(), // Conditionally enable drag
        breakpoints: {
          1099: {
            perPage: 2,
            gap: '24px',
            padding: { right: '48px' },
            drag: true, // Always enabled on mobile
          },
          600: {
            perPage: 1,
            gap: '16px',
            padding: { right: '48px' },
            drag: true, // Always enabled on mobile
          }
        },
        i18n: {
          prev: 'Previous product',
          next: 'Next product',
          first: 'Go to first product',
          last: 'Go to last product',
          slideX: 'Go to product %s',
          pageX: 'Go to page %s',
          play: 'Start autoplay',
          pause: 'Pause autoplay',
          carousel: 'Products carousel',
          select: 'Select a product to view',
          slide: 'product',
          slideLabel: '%s of %s',
        }
      },
      {
        customArrows: true,
        scrollbar: true,
        tabindex: true
      }
    );

    // Update slider state based on conditions
    const updateSlider = () => {
      if (!splideInstance) return;

      const shouldEnable = shouldEnableSlider();

      if (shouldEnable) {
        container.classList.add('slider-active');
        // Enable drag on desktop
        splideInstance.options.drag = true;
        splideInstance.refresh();
      } else {
        container.classList.remove('slider-active');
        // Disable drag on desktop when not enough blocks
        splideInstance.options.drag = false;
        // Go to first slide when disabling
        splideInstance.go(0);
        splideInstance.refresh();
      }
    };

    // Initial setup
    updateSlider();

    // Handle resize
    let resizeTimeout;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(updateSlider, 250);
    };
    window.addEventListener('resize', handleResize);

    // Return destroy function for cleanup
    return {
      destroy: () => {
        window.removeEventListener('resize', handleResize);
        if (splideInstance) splideInstance.destroy();
      }
    };
  };

  /**
   * Handle direct add-to-cart upsells
   */
  function initDirectUpsells(context) {
    const buttons = context.querySelectorAll('.js-add-upsell-to-cart');

    buttons.forEach((button) => {
      if (button.dataset.upsellInitialized === 'true') return;
      button.dataset.upsellInitialized = 'true';

      button.addEventListener('click', function (e) {
        e.preventDefault();
        const variantId = this.getAttribute('data-variant-id');
        const addButton = this;

        // Add loading state
        addButton.classList.add('is-loading');
        addButton.disabled = true;

        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: variantId, quantity: 1 }),
        })
          .then((response) => response.json())
          .then((data) => {
            // Remove loading state
            addButton.classList.remove('is-loading');
            addButton.disabled = false;

            // Dispatch the same event that the main product form uses
            document.dispatchEvent(
              new CustomEvent('ajaxProduct:added', {
                detail: {
                  product: data,
                  addToCartBtn: addButton,
                },
              })
            );
          })
          .catch((error) => {
            console.error('Error adding upsell to cart:', error);
            addButton.classList.remove('is-loading');
            addButton.disabled = false;
          });
      });
    });
  }

  /**
   * Handle toggle upsells - sync with form submission
   */
  function initToggleUpsells(context) {
    const forms = context.querySelectorAll('[data-product-form]');

    forms.forEach(function (form) {
      if (form.dataset.toggleUpsellInitialized === 'true') return;
      form.dataset.toggleUpsellInitialized = 'true';

      const upsellInputsContainer = form.querySelector('[data-upsell-inputs]');
      const card = form.closest('[data-products-list-cta-card]');
      if (!card) return;

      const toggleCheckboxes = card.querySelectorAll('.js-upsell-toggle');

      if (!upsellInputsContainer || toggleCheckboxes.length === 0) return;

      // Intercept form submission to handle bundled items
      form.addEventListener('submit', function (e) {
        const selectedUpsells = Array.from(toggleCheckboxes).filter((cb) => cb.checked);

        if (selectedUpsells.length > 0) {
          e.preventDefault();
          e.stopImmediatePropagation();

          const submitButton = form.querySelector('[type="submit"]');
          if (submitButton) {
            submitButton.classList.add('is-loading');
            submitButton.disabled = true;
          }

          // Build items array with main product and upsells
          const mainVariantId = form.querySelector('[name="id"]').value;
          const quantity = form.querySelector('[name="quantity"]')?.value || 1;

          const items = [{ id: parseInt(mainVariantId), quantity: parseInt(quantity) }];

          selectedUpsells.forEach(function (checkbox) {
            items.push({
              id: parseInt(checkbox.dataset.variantId),
              quantity: 1,
            });
          });

          // Add all items to cart
          fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: items }),
          })
            .then((response) => response.json())
            .then((data) => {
              if (submitButton) {
                submitButton.classList.remove('is-loading');
                submitButton.disabled = false;
              }

              // Keep checkboxes checked to maintain selection state

              // Dispatch event for cart drawer/notification
              document.dispatchEvent(
                new CustomEvent('ajaxProduct:added', {
                  detail: {
                    product: data,
                    addToCartBtn: submitButton,
                    items: items,
                  },
                })
              );
            })
            .catch((error) => {
              console.error('Error adding products to cart:', error);
              if (submitButton) {
                submitButton.classList.remove('is-loading');
                submitButton.disabled = false;
              }
            });
        }
      });
    });
  }

  function initProductsListCta(scope) {
    const context = scope || document;
    const sections = context.querySelectorAll('[data-products-list-cta-section]');

    sections.forEach((section) => {
      // Initialize toggle upsells FIRST so their submit listener fires before AjaxProduct's
      initToggleUpsells(section);
      initDirectUpsells(section);

      const cards = section.querySelectorAll('[data-products-list-cta-card]');

      cards.forEach((card) => {
        if (card.dataset.productsListCtaInitialized === 'true') return;
        card.dataset.productsListCtaInitialized = 'true';
        new ProductsListCtaCard(card, section);
      });

      // Initialize slider
      section.sliderInstance = window.initProductsListCtaSlider(section);
    });
  }

  document.addEventListener('DOMContentLoaded', () => {
    initProductsListCta(document);
  });

  // Setup theme editor events
  if (typeof window.SplideUtils !== 'undefined') {
    window.SplideUtils.setupThemeEditorEvents(
      'ProductsListCta-',
      '.js-products-list-cta-slider',
      (sectionElement) => {
        const container = sectionElement.querySelector('[id^="ProductsListCta-"]');
        if (container) {
          if (container.sliderInstance?.destroy) {
            container.sliderInstance.destroy();
          }
          container.sliderInstance = initProductsListCtaSlider(container);
        }
      }
    );
  }

  document.addEventListener('shopify:section:load', (event) => {
    initProductsListCta(event.target);
  });
})();